import { useState } from 'react';
import { INVESTMENT_HORIZON_YEARS, type InvestmentResult } from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import { useStableId } from '../../../../ds/hooks';
import { Button, Segmented } from '../../../../ds/components';
import { SignedLineChart, type AxisMark } from '../charts';
import {
  calendarYear,
  cumulativeSentence,
  eur,
  journeySentence,
  monthlyOwnShare,
  saleOutcomes,
  sgn,
  wealthSentence,
  yearLedger,
  type LedgerRow,
} from '../model';
import { LEDGER_TONE, LedgerList, Section } from './parts';
import styles from '../advisor.module.css';

const JOURNEY_YEARS = [1, 10, 20, 30] as const;
const AXIS_YEARS = [1, 10, 20, 30] as const;

type ResultTab = 'yearly' | 'cumulative' | 'wealth';
const RESULT_TABS: { value: ResultTab; label: string }[] = [
  { value: 'yearly', label: 'Je Jahr' },
  { value: 'cumulative', label: 'Aufsummiert' },
  { value: 'wealth', label: 'Vermögen' },
];

export interface YearSectionsProps {
  result: InvestmentResult;
  purchasePriceCents: number;
  purchaseYear: number;
  onCopySummary: () => void;
}

function Stack({ rows, totalCents, label }: { rows: LedgerRow[]; totalCents: number; label: string }) {
  const sum = totalCents || 1;
  return (
    <div className={styles.stackCol}>
      <div
        className={styles.stack}
        role="img"
        aria-label={`${label}: ${rows.map((row) => `${row.label} ${eur(row.cents)}`).join(', ')}`}
      >
        {rows.map((row) => (
          <span
            key={row.key}
            className={clsx(styles.stackSegment, LEDGER_TONE[row.key])}
            style={{ flexGrow: Math.max(0, row.cents) / sum, flexBasis: 0 }}
          />
        ))}
      </div>
      <span className={styles.stackLabel}>{label}</span>
    </div>
  );
}

/** Sections 6 „Ihr Weg mit dieser Wohnung" to 9 „Wenn Sie verkaufen"; the selected year is local state. */
export function YearSections({ result, purchasePriceCents, purchaseYear, onCopySummary }: YearSectionsProps) {
  const [year, setYear] = useState(1);
  const [tab, setTab] = useState<ResultTab>('yearly');
  const sliderId = useStableId();
  const ledger = yearLedger(result, year);
  const pays = ledger.ownShareCents > 0;
  const cal = (n: number) => calendarYear(purchaseYear, n);
  const marks: AxisMark[] = AXIS_YEARS.map((n) => ({ index: n - 1, label: String(cal(n)) }));

  const cashflows = result.years.map((row) => row.cashflowCents);
  const peak = Math.max(...cashflows.map((value) => Math.abs(value)), 1);
  const maxUp = Math.max(...cashflows, 0);
  const maxDown = Math.max(...cashflows.map((value) => -value), 0);
  const range = maxUp + maxDown || peak;
  const zeroFromTop = maxUp / range;

  return (
    <>
      <Section
        id="advisor-journey"
        title="Ihr Weg mit dieser Wohnung"
        aside={
          <Button variant="secondary" size="small" onClick={onCopySummary}>
            Zusammenfassung kopieren
          </Button>
        }
      >
        <p className={styles.lead}>{journeySentence(result, purchaseYear)}</p>
        <div className={styles.journeyTiles} role="group" aria-label="Jahre vergleichen">
          {JOURNEY_YEARS.map((n) => {
            const own = monthlyOwnShare(result, n);
            const surplus = own < 0;
            const active = n === year;
            return (
              <button
                key={n}
                type="button"
                aria-pressed={active}
                className={clsx(
                  'pressable',
                  styles.journeyTile,
                  active && (surplus ? styles.journeyActiveSurplus : styles.journeyActivePay),
                )}
                onClick={() => setYear(n)}
              >
                <span className={styles.label}>
                  Jahr {n} · {cal(n)}
                </span>
                <span className={styles.value}>{eur(Math.abs(own))}</span>
                <span className={clsx(styles.caption, surplus ? styles.journeySurplus : styles.journeyPay)}>
                  {surplus ? `${sgn(-own)} € Überschuss im Monat` : 'Sie zahlen im Monat zu'}
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section id="advisor-year" title="Betrachtetes Jahr">
        <div className={styles.card}>
          <div className={styles.yearHead}>
            <div>
              <div className={styles.yearBig}>{cal(year)}</div>
              <div className={styles.caption}>{year}. Jahr nach dem Kauf</div>
            </div>
            <div className={styles.sliderBox}>
              <label htmlFor={sliderId} className={styles.sliderLabel}>
                Jahr wählen
              </label>
              <input
                id={sliderId}
                type="range"
                className={styles.range}
                min={1}
                max={INVESTMENT_HORIZON_YEARS}
                step={1}
                value={year}
                aria-valuetext={`${cal(year)}, ${year}. Jahr`}
                onChange={(event) => setYear(Number(event.target.value))}
              />
              <div className={styles.ticks} aria-hidden="true">
                {AXIS_YEARS.map((n) => (
                  <span key={n} style={{ left: `${((n - 1) / (INVESTMENT_HORIZON_YEARS - 1)) * 100}%` }}>
                    {cal(n)}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.yearBody}>
            <div className={styles.stacks}>
              <Stack rows={ledger.income} totalCents={ledger.totalCents} label="Einnahmen" />
              <Stack rows={ledger.expenses} totalCents={ledger.totalCents} label="Ausgaben" />
            </div>
            <div className={styles.yearRight}>
              <div className={styles.yearTiles}>
                <div className={clsx(styles.yearTile, pays ? styles.yearTilePay : styles.yearTileSurplus)}>
                  <span className={styles.label}>
                    {pays ? 'Ø Ihr Eigenanteil im Monat' : 'Ø Überschuss im Monat'}
                  </span>
                  <span className={styles.value}>{eur(Math.abs(ledger.ownShareMonthlyCents))}</span>
                </div>
                <div className={styles.yearTile}>
                  <span className={styles.label}>Ø Monatsbelastung brutto</span>
                  <span className={styles.value}>{eur(ledger.grossMonthlyCents)}</span>
                  <span className={styles.caption}>Zinsen, Tilgung und Kosten</span>
                </div>
              </div>
              <div className={styles.dlGrid}>
                <LedgerList title="Einnahmen im Jahr" rows={ledger.income} totalCents={ledger.totalCents} />
                <LedgerList title="Ausgaben im Jahr" rows={ledger.expenses} totalCents={ledger.totalCents} />
              </div>
              {ledger.repaymentAsCost ? null : (
                <p className={styles.note}>
                  Die Tilgung von {eur(ledger.principalCents)} im Jahr ist hier nicht als Ausgabe gerechnet:
                  sie baut Ihr Vermögen auf.
                </p>
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section id="advisor-result" title="Ihr Ergebnis über 30 Jahre">
        <div className={styles.card}>
          <div className={styles.resultHead}>
            <Segmented<ResultTab> label="Darstellung" items={RESULT_TABS} value={tab} onChange={setTab} />
          </div>
          {tab === 'yearly' ? (
            <>
              <div className={styles.barsScroll}>
                <div className={styles.barsInner}>
                  <div className={styles.bars}>
                    <span
                      className={styles.barZero}
                      style={{ top: `${zeroFromTop * 100}%` }}
                      aria-hidden="true"
                    />
                    {result.years.map((row) => {
                      const value = row.cashflowCents;
                      const surplus = value >= 0;
                      const height = (Math.abs(value) / range) * 100;
                      const selected = row.year === year;
                      return (
                        <button
                          key={row.year}
                          type="button"
                          aria-pressed={selected}
                          aria-label={`${cal(row.year)}: ${surplus ? 'Überschuss' : 'Zuzahlung'} ${eur(Math.abs(value))}`}
                          className={clsx('pressable', styles.barButton, selected && styles.barSelected)}
                          onClick={() => setYear(row.year)}
                        >
                          <span
                            className={clsx(styles.bar, surplus ? styles.barSurplus : styles.barPay)}
                            style={
                              surplus
                                ? { bottom: `${(1 - zeroFromTop) * 100}%`, height: `${height}%` }
                                : { top: `${zeroFromTop * 100}%`, height: `${height}%` }
                            }
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className={styles.barAxis} aria-hidden="true">
                    {result.years.map((row) => (
                      <span key={row.year}>
                        {(AXIS_YEARS as readonly number[]).includes(row.year) ? cal(row.year) : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={clsx(styles.swatch, styles.barPay)} aria-hidden="true" />
                  Zuzahlung im Jahr
                </span>
                <span className={styles.legendItem}>
                  <span className={clsx(styles.swatch, styles.barSurplus)} aria-hidden="true" />
                  Überschuss im Jahr
                </span>
              </div>
              <p className={clsx(styles.note, styles.chartNote)}>
                Ergebnis nach Steuern je Jahr. Tippen Sie auf einen Balken, um das Jahr oben anzusehen.
              </p>
            </>
          ) : null}
          {tab === 'cumulative' ? (
            <>
              <SignedLineChart
                label="Aufsummiertes Ergebnis nach Steuern über 30 Jahre"
                series={[
                  {
                    label: 'Aufsummiert',
                    tone: 'cumulative',
                    values: result.years.map((row) => row.cumulativeCashflowCents),
                  },
                ]}
                marks={marks}
                marker={year - 1}
              />
              <p className={clsx(styles.note, styles.chartNote)}>
                {cumulativeSentence(result, purchaseYear)}
              </p>
            </>
          ) : null}
          {tab === 'wealth' ? (
            <>
              <SignedLineChart
                label="Wohnungswert, Restschuld und Ihr Vermögen über 30 Jahre"
                series={[
                  {
                    label: 'Wohnungswert',
                    tone: 'value',
                    dashed: true,
                    values: result.years.map((row) => row.propertyValueCents),
                  },
                  {
                    label: 'Restschuld',
                    tone: 'debt',
                    values: result.years.map((row) => row.remainingDebtCents),
                  },
                ]}
                marks={marks}
                marker={year - 1}
                band
              />
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={clsx(styles.legendLine, styles.legendLineDashed)} aria-hidden="true" />
                  Wohnungswert
                </span>
                <span className={styles.legendItem}>
                  <span className={clsx(styles.legendLine, styles.legendLineDebt)} aria-hidden="true" />
                  Restschuld
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendBand} aria-hidden="true" />
                  Ihr Vermögen
                </span>
              </div>
              <p className={clsx(styles.note, styles.chartNote)}>
                {wealthSentence(result, year, purchaseYear)}
              </p>
            </>
          ) : null}
        </div>
      </Section>

      <Section id="advisor-sale" title="Wenn Sie verkaufen">
        <div className={styles.sale}>
          {saleOutcomes(result, purchasePriceCents).map((outcome) => (
            <div key={outcome.horizon} className={styles.saleCol}>
              <span className={styles.label}>{outcome.label}</span>
              <span className={styles.saleValue}>{eur(outcome.returnCents)}</span>
              <span className={styles.caption}>{outcome.caption}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
